import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDeal } from "../lib/store";
import { docsApi } from "../lib/api";
import { Card } from "../components/shared";
import { dealKeyFor } from "../components/ShareRoomModal";
import { Upload, Download, Trash2, Pencil, FolderOpen, History, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Lender Teaser", "Investor Teaser", "Pitch Deck", "Financials", "Tax Returns", "Inventory", "Legal/NDA", "Other"];
const FIRM_KEY = "firm";

const DocRow = ({ d, latest, old, onRename, onDelete }) => (
  <div className={`doc-row${old ? " doc-row-old" : ""}`} data-testid={`doc-row-${d.id}`}>
    <div className="doc-row-main">
      <b>{d.label}{d.version > 1 ? <em className="doc-ver">v{d.version}</em> : null}{latest ? <em className="doc-latest">Latest</em> : null}</b>
      <span>{d.filename} · {d.size < 1024 ? "<1 KB" : `${(d.size / 1024).toFixed(0)} KB`} · {String(d.uploadedAt || "").slice(0, 10)}{d.uploadedBy ? ` · ${d.uploadedBy}` : ""}{d.downloads ? ` · ${d.downloads} download${d.downloads > 1 ? "s" : ""}` : ""}</span>
    </div>
    <div className="doc-row-acts">
      <button className="btn btn-sm" onClick={() => docsApi.download(d.id, d.filename)} data-testid={`doc-download-${d.id}`}><Download size={13} /></button>
      <button className="btn btn-sm" onClick={() => onRename(d)} data-testid={`doc-rename-${d.id}`}><Pencil size={13} /></button>
      <button className="btn btn-sm" onClick={() => onDelete(d)} data-testid={`doc-delete-${d.id}`}><Trash2 size={13} /></button>
    </div>
  </div>
);

// One document = its newest version. Superseded uploads stay one click away.
const DocChain = ({ chain, onRename, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [current, ...older] = chain;
  return (
    <div data-testid={`doc-chain-${current.id}`}>
      <DocRow d={current} latest={current.version > 1} onRename={onRename} onDelete={onDelete} />
      {older.length ? (
        <button className="doc-older-toggle" onClick={() => setOpen((o) => !o)} data-testid={`doc-older-${current.id}`}>
          <History size={11} />{open ? "Hide" : "Show"} {older.length} older version{older.length > 1 ? "s" : ""}
          <ChevronDown size={11} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.18s ease" }} />
        </button>
      ) : null}
      {open ? <div className="doc-older" data-testid={`doc-older-list-${current.id}`}>{older.map((d) => <DocRow key={d.id} d={d} old onRename={onRename} onDelete={onDelete} />)}</div> : null}
    </div>
  );
};

export default function DocsRoom() {
  const { M, dealId, dealName } = useDeal();
  const dealKey = dealKeyFor(dealId, M);
  const [shelf, setShelf] = useState("deal");
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("Financials");
  const [label, setLabel] = useState("");
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => docsApi.list({ dealKey }).then((r) => setDocs(r.docs || [])).catch(() => setDocs([]));
  useEffect(() => { load(); }, [dealKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const shelfKey = shelf === "firm" ? FIRM_KEY : dealKey;
  const shown = useMemo(() => docs.filter((d) => (shelf === "firm" ? d.dealKey === FIRM_KEY : d.dealKey === dealKey)), [docs, shelf, dealKey]);
  const byCat = useMemo(() => CATEGORIES.map((c) => {
    const list = shown.filter((d) => d.category === c);
    const chains = [];
    const idx = new Map();
    [...list].sort((a, b) => (b.version || 0) - (a.version || 0)).forEach((d) => {
      if (!idx.has(d.label)) { idx.set(d.label, chains.length); chains.push([]); }
      chains[idx.get(d.label)].push(d);
    });
    return [c, chains];
  }).filter(([, chains]) => chains.length), [shown]);

  const upload = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    try {
      for (const f of list) {
        await docsApi.upload(f, { dealKey: shelfKey, dealName: shelf === "firm" ? "" : (dealName || M.dealName || ""), category, label: list.length === 1 ? label : "" });
      }
      toast.success(`Uploaded ${list.length} file${list.length > 1 ? "s" : ""} to ${category}`);
      setLabel("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const onRename = async (d) => {
    const next = window.prompt("Document name", d.label);
    if (next === null) return;
    await docsApi.patch(d.id, { label: next });
    await load();
    toast.success("Renamed");
  };

  const onDelete = async (d) => {
    if (!window.confirm(`Remove “${d.label}” from the Docs Room?`)) return;
    await docsApi.remove(d.id);
    await load();
    toast.success("Removed");
  };

  return (
    <div className="page" data-testid="page-docs">
      <div className="page-head">
        <div className="page-title">Docs Room</div>
        <div className="page-sub">Every document for this deal in one place — attach any of them straight into a lender or investor email</div>
      </div>

      <Card title="Library" testid="docs-library" right={(
        <div className="tone-bar" data-testid="docs-shelf">
          <button className={shelf === "deal" ? "active" : ""} onClick={() => setShelf("deal")} data-testid="docs-shelf-deal">This deal</button>
          <button className={shelf === "firm" ? "active" : ""} onClick={() => setShelf("firm")} data-testid="docs-shelf-firm">Firm-wide</button>
        </div>
      )}>
        <div className="ir-fine" style={{ marginBottom: 10 }} data-testid="docs-shelf-note">
          {shelf === "firm"
            ? "Evergreen documents that apply to every deal — firm overview, track record, standard NDA."
            : `Documents attached to ${dealName || M.dealName || "this deal"}. Tax returns, financials, inventory and the teasers you generate live here.`}
        </div>

        <div className="docs-upload">
          <div className="field" style={{ minWidth: 190 }}>
            <label>Category</label>
            <select className="inp" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="docs-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label>Name (optional)</label>
            <input className="inp inp-text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. 2024 Federal return — 1120S" data-testid="docs-label" />
          </div>
        </div>
        <div className={`docs-drop${over ? " over" : ""}`} data-testid="docs-drop"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); upload(e.dataTransfer.files); }}>
          <Upload size={15} />
          <span>{busy ? "Uploading…" : "Drag files here, or click to choose — PDF, Excel, Word, PowerPoint, images (25 MB max)"}</span>
          <input ref={fileRef} type="file" multiple hidden onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="docs-file-input" />
        </div>

        {byCat.length === 0
          ? <div className="docs-empty" data-testid="docs-empty"><FolderOpen size={16} /> Nothing here yet. Upload a document, or hit “Save to Docs Room” in the Teaser Generator.</div>
          : byCat.map(([cat, chains]) => (
            <div key={cat} className="docs-group" data-testid={`docs-group-${cat.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
              <div className="section-label">{cat} · {chains.length}</div>
              {chains.map((chain) => <DocChain key={chain[0].id} chain={chain} onRename={onRename} onDelete={onDelete} />)}
            </div>
          ))}
      </Card>
    </div>
  );
}
